/* eslint-disable import/first */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Icon, Loader, Message } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../styles/glassmorphism';
import DashboardService from './services/DashboardService';
import { DashboardMetricsDto } from './types/dashboard.types';
import KpiCard from './components/KpiCard';
import MetricsChart from './components/MetricsChart';
import ConversionFunnel from './components/ConversionFunnel';
import QualityIndicator from './components/QualityIndicator';

const DashboardWhatsAppPage: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetricsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // ===== LOAD METRICS =====
  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DashboardService.getDashboardMetrics(days);
      setMetrics(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  }, [days]);

  // ===== INITIAL LOAD & AUTO REFRESH =====
  useEffect(() => {
    loadMetrics();
    
    // Auto-refresh a cada 5 minutos
    const interval = setInterval(loadMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadMetrics]);

  // ===== HANDLE RESIZE =====
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== PERIOD BUTTONS =====
  const periodButtons = [
    { label: 'Hoje', days: 1 },
    { label: '7 dias', days: 7 },
    { label: '30 dias', days: 30 },
  ];

  return (
    <div style={{
      background: GlassmorphismTheme.colors.gradients.primary,
      minHeight: '100vh',
      padding: isMobile ? GlassmorphismTheme.spacing.sm : GlassmorphismTheme.spacing.md,
    }}>
      {/* HEADER */}
      <div style={{
        marginBottom: GlassmorphismTheme.spacing.lg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: GlassmorphismTheme.spacing.md,
      }}>
        <h1 style={{
          margin: 0,
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem'
        }}>
          <Icon name="chart bar" />
          {isMobile ? 'Dashboard' : 'Dashboard WhatsApp'}
        </h1>

        {/* REFRESH BUTTON & TIMESTAMP */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: GlassmorphismTheme.spacing.md,
          fontSize: isMobile ? '0.75rem' : '0.85rem',
          flexWrap: 'wrap'
        }}>
          <small style={{ color: '#7f8c8d' }}>
            {isMobile ? 'Atualizado:' : 'Última atualização:'} {lastRefresh.toLocaleTimeString('pt-BR')}
          </small>
          <Button
            icon
            onClick={loadMetrics}
            loading={loading}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            style={{
              ...GlassmorphismTheme.components.buttonGlass,
              minWidth: isMobile ? '35px' : '40px'
            }}
            title="Atualizar dados"
          >
            <Icon name="sync alternate" />
          </Button>
        </div>
      </div>

      {/* PERIOD FILTER */}
      <div style={{
        marginBottom: GlassmorphismTheme.spacing.lg,
        display: 'flex',
        gap: GlassmorphismTheme.spacing.sm,
        flexWrap: 'wrap',
        justifyContent: isMobile ? 'center' : 'flex-start'
      }}>
        {periodButtons.map(btn => (
          <Button
            key={btn.days}
            onClick={() => setDays(btn.days)}
            active={days === btn.days}
            size={isMobile ? 'small' : 'medium'}
            style={{
              ...GlassmorphismTheme.components.buttonGlass,
              background: days === btn.days ? 'rgba(102, 126, 234, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              borderColor: days === btn.days ? '#667eea' : 'rgba(255, 255, 255, 0.3)',
              color: days === btn.days ? '#667eea' : '#2c3e50',
              fontWeight: days === btn.days ? 600 : 400,
              minWidth: isMobile ? '60px' : '80px'
            }}
          >
            {btn.label}
          </Button>
        ))}
      </div>

      {/* ERROR STATE */}
      {error && (
        <Message
          error
          style={{
            ...GlassmorphismTheme.components.glassCard,
            marginBottom: GlassmorphismTheme.spacing.lg
          }}
        >
          <Message.Header>Erro ao carregar dashboard</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* LOADING STATE */}
      {loading && !metrics ? (
        <Loader
          active
          size="large"
          content="Carregando métricas..."
          style={{ marginTop: '50px' }}
        />
      ) : metrics ? (
        <>
          {/* KPI CARDS - TOP ROW */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? GlassmorphismTheme.spacing.sm : GlassmorphismTheme.spacing.md,
            marginBottom: GlassmorphismTheme.spacing.lg
          }}>
            <KpiCard
              title="Total Conversas"
              value={metrics.conversations.total}
              icon="comments"
              type="status"
              status={metrics.conversations.total > 100 ? 'good' : 'warning'}
            />
            <KpiCard
              title="Taxa Conversão"
              value={metrics.offers.conversionRate.toFixed(1)}
              unit="%"
              icon="chart pie"
              type="conversion"
              status={metrics.offers.conversionRate > 30 ? 'good' : metrics.offers.conversionRate > 15 ? 'warning' : 'critical'}
            />
            <KpiCard
              title="Tempo Médio"
              value={metrics.conversations.averageResponseTimeMinutes.toFixed(0)}
              unit="min"
              icon="clock"
              type="responseTime"
              status={metrics.conversations.averageResponseTimeMinutes < 30 ? 'good' : 'warning'}
            />
            <KpiCard
              title="Qualidade"
              value={Math.round(metrics.quality.greenNumbersPercentage)}
              unit="%"
              icon="signal"
              type="quality"
              status={metrics.quality.greenNumbersPercentage > 80 ? 'good' : metrics.quality.greenNumbersPercentage > 50 ? 'warning' : 'critical'}
            />
          </div>

          {/* FUNNEL & QUALITY ROW */}
          <div style={{
            display: isMobile ? 'block' : 'flex',
            gap: GlassmorphismTheme.spacing.md,
            marginBottom: GlassmorphismTheme.spacing.lg,
            width: '100%',
            alignItems: 'stretch'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ConversionFunnel metrics={metrics.conversations} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <QualityIndicator metrics={metrics.quality} />
            </div>
          </div>

          {/* METRICS CHARTS ROW */}
          <div style={{
            display: isMobile ? 'block' : 'flex',
            gap: GlassmorphismTheme.spacing.md,
            marginBottom: GlassmorphismTheme.spacing.lg,
            width: '100%'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <MetricsChart data={metrics.timeSeries} metric="sent" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <MetricsChart data={metrics.timeSeries} metric="delivered" />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default DashboardWhatsAppPage;
